import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, inr, formatApiError } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { getAttribution, track } from "../lib/tracking";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Logo } from "../components/site/Seo";
import { toast } from "sonner";
import { CheckCircle2, Tag, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";

export default function Checkout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings } = useSite();
  const [p, setP] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      if (r.data.owned) { nav(`/student/series/${r.data.id}`); return; }
      setP(r.data);
      track("InitiateCheckout", { content_name: r.data.name, value: r.data.price, currency: "INR" });
    }).catch(() => nav("/pricing"));
  }, [slug]);

  if (!p) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-ember" /></div>;

  const discount = applied?.discount || 0;
  const subtotal = p.price - discount;
  const gst = Math.round(subtotal * (p.gst || 0) / 100 * 100) / 100;
  const total = subtotal + gst;

  const applyCoupon = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, product_id: p.id });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied! You save ${inr(data.discount)}`);
    } catch (err) { setApplied(null); toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/orders", {
        product_id: p.id, coupon_code: applied?.code || null,
        gateway: "razorpay", attribution: getAttribution(),
      });
      track("AddPaymentInfo", { value: data.total, currency: "INR" });
      if (data.simulation) {
        // No gateway keys configured — safe simulated verification
        await api.post("/payments/verify", { order_id: data.order_id });
        track("Purchase", { value: data.total, currency: "INR", content_name: p.name });
        toast.success("Payment successful (test mode)! Access granted.");
        nav(`/student/series/${p.id}`);
      } else {
        await launchRazorpay(data);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPaying(false); }
  };

  const launchRazorpay = (data) => new Promise((resolve) => {
    const start = () => {
      const rzp = new window.Razorpay({
        key: data.key_id, amount: Math.round(data.total * 100), currency: "INR",
        name: settings.brand_name || "Agni Shiksha", description: data.product_name,
        order_id: data.gateway_order_id,
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              order_id: data.order_id, razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id, razorpay_signature: resp.razorpay_signature,
            });
            track("Purchase", { value: data.total, currency: "INR", content_name: p.name });
            toast.success("Payment successful! Access granted.");
            nav(`/student/series/${p.id}`);
          } catch (e) { toast.error("Verification failed. Contact support if charged."); setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: "#F97316" },
      });
      rzp.open();
    };
    if (window.Razorpay) { start(); resolve(); }
    else {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => { start(); resolve(); };
      document.body.appendChild(s);
    }
  });

  return (
    <div className="min-h-screen bg-ink container-px py-8 hero-glow">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to={`/product/${p.slug}`} className="text-sm text-content-secondary flex items-center gap-1 hover:text-ember"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <Logo className="h-8" />
        </div>
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 card-surface p-6">
            <h1 className="font-heading text-xl font-bold mb-1">Checkout</h1>
            <p className="text-sm text-content-secondary mb-6">You're one step away from full access.</p>
            <div className="flex items-start gap-4 pb-5 border-b border-line">
              <div className="w-12 h-12 rounded-xl bg-ember-soft flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-ember" /></div>
              <div>
                <div className="font-medium text-sm">{p.name}</div>
                <div className="text-xs text-content-muted mt-0.5">{p.num_tests}+ tests • {p.num_questions?.toLocaleString("en-IN")}+ MCQs • {Math.round((p.validity_days||365)/30)} months</div>
              </div>
            </div>
            <div className="py-5">
              <label className="text-sm font-medium flex items-center gap-1.5 mb-2"><Tag className="w-4 h-4 text-ember" /> Have a coupon?</label>
              <div className="flex gap-2">
                <Input placeholder="Enter code (try AGNI50)" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="bg-ink border-line" data-testid="coupon-input" />
                <Button variant="outline" onClick={applyCoupon} disabled={busy || !coupon} className="border-line rounded-lg" data-testid="coupon-apply">Apply</Button>
              </div>
              {applied && <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {applied.code} applied — you save {inr(applied.discount)}</p>}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="card-surface p-6">
              <h3 className="font-heading font-semibold mb-4">Order summary</h3>
              <Row label="Price" value={inr(p.price)} />
              {discount > 0 && <Row label={`Discount (${applied.code})`} value={`- ${inr(discount)}`} accent />}
              {p.gst ? <Row label={`GST (${p.gst}%)`} value={inr(gst)} /> : null}
              <div className="border-t border-line my-3" />
              <Row label="Total" value={inr(total)} big />
              <Button onClick={pay} disabled={paying} className="btn-ember rounded-full w-full h-12 mt-5" data-testid="checkout-pay">
                {paying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</> : `Pay ${inr(total)}`}
              </Button>
              <p className="text-[11px] text-content-muted text-center mt-3">Secure server-side verified payment. Access is granted instantly.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
const Row = ({ label, value, big, accent }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={`text-sm ${big ? "font-heading font-semibold text-base" : "text-content-secondary"}`}>{label}</span>
    <span className={`${big ? "font-heading font-bold text-lg" : "text-sm"} ${accent ? "text-emerald-400" : ""}`}>{value}</span>
  </div>
);
